import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useId, useState, type ReactNode } from 'react'

import { formatPesewas } from '../../lib/money'
import { useAuth } from '../AuthContext'
import { ApiError } from '../api/client'
import {
  ISSUED_REFERENCE,
  REFUNDS_KEY,
  fetchRefundsDue,
  markRefundIssued,
  type RefundDueItem,
} from '../api/transportBookings'
import { EmptyState } from '../components/EmptyState'
import { ListDisclosure } from '../components/ListDisclosure'
import { accraDay, refundReasonWords, stamp } from '../transportDesk'

/**
 * Refunds due on transport bookings (M40-T12, spec §F.2 step 6, T05), for the
 * roles holding `transport_refund:mark_issued`.
 *
 * A record, not money: the payment side returns the money, and this is where
 * staff note that it has, with its reference and the day. Not issued first,
 * oldest first, since those are the ones still owed.
 */

const INPUT =
  'w-full rounded-[8px] border border-line-2 bg-bg-app px-2 py-1.5 text-sm text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'
const BUTTON =
  'inline-flex h-9 items-center justify-center rounded-[8px] border border-line-2 bg-bg-app px-3.5 text-sm font-medium text-ink-1 hover:bg-bg-soft disabled:opacity-50'
const PRIMARY =
  'inline-flex h-9 items-center justify-center rounded-[8px] bg-accent px-3.5 text-sm font-semibold text-accent-on hover:opacity-90 disabled:opacity-50'

function hintClass(broken: boolean): string {
  return broken ? 'text-xs font-medium text-bed-bad' : 'text-xs text-ink-3'
}

export function TransportRefundsTab(): ReactNode {
  return (
    <div className="flex flex-col gap-6">
      <p className="text-sm text-ink-2">
        What Medcor owes on transport bookings. The money is returned through
        the payment side; mark each one issued here once it has been, with the
        payment side’s reference.
      </p>
      <RefundList issued={false} />
      <RefundList issued />
    </div>
  )
}

function RefundList({ issued }: { issued: boolean }): ReactNode {
  const { token } = useAuth()
  const list = useQuery({
    queryKey: [...REFUNDS_KEY, issued],
    queryFn: () => fetchRefundsDue(token, issued),
    enabled: Boolean(token),
  })
  const title = issued ? 'Issued' : 'Not issued'
  const headingId = issued ? 'refunds-issued' : 'refunds-waiting'

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-2">
      <h2
        id={headingId}
        className="font-display text-lg font-semibold text-ink-1"
      >
        {title}
      </h2>
      {list.isPending ? (
        <div
          role="status"
          aria-label={`Loading: ${title}`}
          className="h-20 rounded-2xl bg-bg-sunken"
        />
      ) : list.isError ? (
        <p role="alert" className="text-sm font-medium text-bed-bad">
          {list.error instanceof ApiError
            ? list.error.message
            : 'Could not load the refunds.'}
        </p>
      ) : list.data.data.length === 0 ? (
        <div className="rounded-2xl border border-line-1">
          <EmptyState title={issued ? 'None issued yet.' : 'Nothing owed.'}>
            {issued
              ? 'Refunds marked issued will be listed here.'
              : 'Every refund due has been marked issued.'}
          </EmptyState>
        </div>
      ) : (
        <>
          <ListDisclosure
            shown={list.data.data.length}
            total={list.data.total}
          />
          <ul className="flex flex-col gap-2">
            {list.data.data.map((r) => (
              <li
                key={r.id}
                className="flex flex-col gap-2 rounded-[12px] border border-line-1 bg-bg-app px-4 py-3 text-sm"
              >
                <RefundRow refund={r} />
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  )
}

function RefundRow({ refund }: { refund: RefundDueItem }): ReactNode {
  const [marking, setMarking] = useState(false)
  return (
    <>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="font-mono font-semibold text-ink-1">
          {refund.reference}
        </span>
        <span className="font-semibold text-ink-1">
          {formatPesewas(refund.amount_pesewas)}
        </span>
        <span className="text-ink-2">{refundReasonWords(refund.reason)}</span>
        <span className="text-ink-3">
          Owed since {stamp(refund.created_at)}
        </span>
        {refund.issued_at ? (
          <span className="text-ink-2">
            Issued {stamp(refund.issued_at)} · {refund.issued_reference}
          </span>
        ) : marking ? null : (
          <button
            type="button"
            className={BUTTON}
            aria-label={`Mark issued: ${refund.reference}`}
            onClick={() => setMarking(true)}
          >
            Mark issued
          </button>
        )}
      </div>
      {marking && !refund.issued_at ? (
        <MarkIssuedForm refund={refund} onCancel={() => setMarking(false)} />
      ) : null}
    </>
  )
}

function MarkIssuedForm({
  refund,
  onCancel,
}: {
  refund: RefundDueItem
  onCancel: () => void
}): ReactNode {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  // The server refuses a day in the future or before the refund was owed.
  const [today] = useState(() => accraDay(Date.now()))
  const owed = accraDay(refund.created_at)
  const [reference, setReference] = useState('')
  const [day, setDay] = useState(today)
  const ref = reference.trim()
  const refOk = ISSUED_REFERENCE.test(ref)
  const dayOk = day !== '' && day >= owed && day <= today
  const refHint = useId()
  const dayHint = useId()

  const save = useMutation({
    mutationFn: () => markRefundIssued(token, refund.id, ref, day),
    // Settled, not only on success: an `already_issued` refusal means the
    // list is out of date too.
    onSettled: () => queryClient.invalidateQueries({ queryKey: REFUNDS_KEY }),
  })

  return (
    <fieldset className="flex flex-col gap-2 rounded-[10px] border border-line-2 p-3">
      <legend className="px-1 text-xs font-semibold text-ink-2">
        Mark {refund.reference} issued
      </legend>
      <label className="flex flex-col gap-1 text-xs font-medium text-ink-2">
        Payment-side reference
        <input
          className={INPUT}
          value={reference}
          maxLength={100}
          aria-invalid={ref !== '' && !refOk}
          aria-describedby={refHint}
          onChange={(e) => setReference(e.target.value)}
        />
      </label>
      {/* Always shown, so each input's description is there before it is
          needed; red once what was typed breaks it. */}
      <p id={refHint} className={hintClass(ref !== '' && !refOk)}>
        Letters, numbers and . _ / - only, as the payment side gave it.
      </p>
      <label className="flex flex-col gap-1 text-xs font-medium text-ink-2">
        Day it was returned
        <input
          type="date"
          className={INPUT}
          value={day}
          min={owed}
          max={today}
          aria-invalid={!dayOk}
          aria-describedby={dayHint}
          onChange={(e) => setDay(e.target.value)}
        />
      </label>
      <p id={dayHint} className={hintClass(!dayOk)}>
        Between the day it was owed and today.
      </p>
      <p className="text-xs text-ink-3">
        Returned a different amount? Mark it issued, and put the amount in a
        note on the booking.
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          className={PRIMARY}
          disabled={!refOk || !dayOk || save.isPending || save.isSuccess}
          onClick={() => save.mutate()}
        >
          Save: issued
        </button>
        <button type="button" className={BUTTON} onClick={onCancel}>
          Cancel
        </button>
      </div>
      {save.isError ? (
        <p role="alert" className="text-sm font-medium text-bed-bad">
          {save.error instanceof ApiError
            ? save.error.message
            : 'Could not mark it issued.'}
        </p>
      ) : null}
    </fieldset>
  )
}
